/**
 * Profily nastavení — samostatná entita mimo běžná nastavení.
 *
 * Profil je pojmenovaná sada přepisů parametrů (viz params.ts) s vlastním
 * vzhledem (barva, ikona), pořadím, viditelností a popisem. Profil může
 * dědit globální přepisy (inheritGlobal), nebo stavět jen na výchozích
 * hodnotách. Citlivé parametry profily nikdy nenesou.
 */
import { sanitizeOverrides, type ParamOverrides } from './params';

export interface Profile {
  id: string;
  name: string;
  /** Barva pro vizuální odlišení v UI. */
  color: string;
  /** Název ikony z ikonového systému, případně emoji (fallback). */
  icon: string;
  description: string;
  /** Pořadí v seznamu (nižší = výš). */
  order: number;
  /** Skryté profily se nenabízejí v přepínači, ale zůstávají uložené. */
  visible: boolean;
  /** true = nepřepsané parametry dědí globální nastavení; false = čisté výchozí. */
  inheritGlobal: boolean;
  overrides: ParamOverrides;
  /** Vestavěný startovací profil (lze upravit i smazat, jen pro označení). */
  builtIn?: boolean;
}

let profileCounter = 0;

function newId(): string {
  profileCounter += 1;
  return `profile-${Date.now().toString(36)}-${profileCounter}`;
}

/** Vestavěné startovací profily — uživatel je může libovolně upravit. */
export function defaultProfiles(): Profile[] {
  return [
    {
      id: 'p-default',
      name: 'Výchozí',
      color: '#3b82f6',
      icon: 'profile',
      description: 'Vyvážené doporučování bez zvláštních preferencí.',
      order: 0,
      visible: true,
      inheritGlobal: true,
      overrides: {},
      builtIn: true,
    },
    {
      id: 'p-fast',
      name: 'Spěchám',
      color: '#e07b16',
      icon: 'gauge',
      description: 'Hlavně rychle — cena a emise jsou vedlejší.',
      order: 1,
      visible: true,
      inheritGlobal: true,
      overrides: {
        'weights.time': 1,
        'weights.cost': 0.1,
        'weights.comfort': 0.2,
        'weights.emissions': 0,
        'sort.key': 'time',
      },
      builtIn: true,
    },
    {
      id: 'p-saver',
      name: 'Úsporný',
      color: '#22a06b',
      icon: 'money',
      description: 'Co nejlevněji, klidně pomaleji a s přestupy.',
      order: 2,
      visible: true,
      inheritGlobal: true,
      overrides: {
        'weights.time': 0.4,
        'weights.cost': 1,
        'weights.comfort': 0.1,
        'weights.emissions': 0.3,
        'sort.key': 'cost',
        'filter.carFree': true,
      },
      builtIn: true,
    },
    {
      id: 'p-eco',
      name: 'Eko & pohoda',
      color: '#0fa3a3',
      icon: 'leaf',
      description: 'Nízké emise a komfort před rychlostí.',
      order: 3,
      visible: true,
      inheritGlobal: true,
      overrides: {
        'weights.time': 0.5,
        'weights.cost': 0.3,
        'weights.comfort': 0.8,
        'weights.emissions': 1,
        'zones.metric': 'emissions',
      },
      builtIn: true,
    },
  ];
}

/** Nový prázdný profil s rozumnými výchozími hodnotami. */
export function createProfile(existing: Profile[], partial?: Partial<Profile>): Profile {
  const maxOrder = existing.reduce((m, p) => Math.max(m, p.order), -1);
  return {
    id: newId(),
    name: 'Nový profil',
    color: '#8a4fff',
    icon: 'star',
    description: '',
    order: maxOrder + 1,
    visible: true,
    inheritGlobal: true,
    overrides: {},
    ...partial,
  };
}

export function duplicateProfile(existing: Profile[], source: Profile): Profile {
  return createProfile(existing, {
    name: `${source.name} (kopie)`,
    color: source.color,
    icon: source.icon,
    description: source.description,
    inheritGlobal: source.inheritGlobal,
    overrides: { ...source.overrides },
  });
}

/** Imutabilní aktualizace profilu podle id. */
export function updateProfile(
  profiles: Profile[],
  id: string,
  patch: Partial<Profile>,
): Profile[] {
  return profiles.map((p) => (p.id === id ? { ...p, ...patch, id: p.id } : p));
}

/** Smazání profilu; vždy musí zůstat alespoň jeden. */
export function removeProfile(profiles: Profile[], id: string): Profile[] {
  if (profiles.length <= 1) return profiles;
  return profiles.filter((p) => p.id !== id);
}

/** Posun profilu v pořadí o delta (-1 nahoru, +1 dolů). Vrací přerovnané pole. */
export function moveProfile(profiles: Profile[], id: string, delta: -1 | 1): Profile[] {
  const sorted = sortedProfiles(profiles);
  const idx = sorted.findIndex((p) => p.id === id);
  const target = idx + delta;
  if (idx < 0 || target < 0 || target >= sorted.length) return profiles;
  const reordered = [...sorted];
  [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
  return reordered.map((p, i) => ({ ...p, order: i }));
}

export function sortedProfiles(profiles: Profile[]): Profile[] {
  return [...profiles].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name, 'cs'));
}

/** Validace profilů z cizího zdroje (import/sdílení/localStorage). */
export function sanitizeProfiles(raw: unknown): Profile[] | null {
  if (!Array.isArray(raw)) return null;
  const out: Profile[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const p = item as Partial<Profile>;
    if (typeof p.id !== 'string' || typeof p.name !== 'string' || p.name.length === 0) continue;
    out.push({
      id: p.id,
      name: p.name.slice(0, 60),
      color: typeof p.color === 'string' ? p.color : '#3b82f6',
      icon: typeof p.icon === 'string' ? p.icon : 'profile',
      description: typeof p.description === 'string' ? p.description.slice(0, 300) : '',
      order: Number.isFinite(p.order) ? (p.order as number) : out.length,
      visible: p.visible !== false,
      inheritGlobal: p.inheritGlobal !== false,
      overrides: sanitizeOverrides(p.overrides),
      builtIn: p.builtIn === true,
    });
  }
  return out.length > 0 ? out : null;
}

export function findProfile(profiles: Profile[], id: string | null): Profile | null {
  if (!id) return null;
  return profiles.find((p) => p.id === id) ?? null;
}
